import { Box, Typography } from '@mui/material';

/**
 * Switching face state.
 *
 * The thumbnails are the client's own captures, so the choice is made by
 * looking rather than by reading: you pick the face you want to see, not a
 * word for it. The label underneath is there for anyone who cannot see the
 * thumbnail clearly, and for the poses that look alike at thumbnail size.
 *
 * A pose that did not feed the current measurement is still offered — it is
 * the client's photo and they can look at any of it — but it is marked, so
 * switching to it does not silently leave an empty drawing with no
 * explanation of why.
 */
export default function PoseSwitcher({ frames, value, onChange, contributing, t }) {
  if (!frames?.length) return null;

  return (
    <Box
      role="tablist"
      aria-label={t('candleCard.visual.poseSwitcher')}
      sx={{
        display: 'flex', gap: 1, overflowX: 'auto', pb: .5,
        // A scrollbar across a row of faces is noise; the row scrolls fine
        // without one on touch, and the overflow is obvious from the clipping.
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {frames.map((f) => {
        const selected = f.pose === value;
        const feeds = !contributing || contributing.includes(f.pose);
        return (
          <Box
            key={f.pose}
            component="button"
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(f.pose)}
            sx={{
              font: 'inherit', p: 0, flex: '0 0 auto', cursor: 'pointer',
              // Wide enough for the Persian names, which are longer than the
              // English ones and were being cut mid-word at 74px.
              width: 92, borderRadius: 2.5, overflow: 'hidden',
              border: '2px solid',
              borderColor: selected ? '#C0A786' : 'transparent',
              background: 'none',
              opacity: feeds || selected ? 1 : 0.55,
              '&:focus-visible': { outline: '2px solid #11223E', outlineOffset: 2 },
            }}
          >
            <Box sx={{
              position: 'relative', width: '100%', aspectRatio: '1 / 1',
              borderRadius: 2, overflow: 'hidden', bgcolor: '#0A1425',
            }}>
              {f.image && (
                <Box component="img" src={f.image} alt="" draggable={false}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              )}
            </Box>
            <Typography sx={{
              fontSize: 10.5, fontWeight: selected ? 800 : 600, py: .5, px: .25,
              color: selected ? 'text.primary' : 'text.secondary',
              lineHeight: 1.25,
              // Two lines, then clamp. Truncating a pose name to 'مع کردن چش'
              // leaves the reader guessing which face they are looking at.
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', minHeight: 28,
            }}>
              {t(`candleCard.pose.${f.pose}`, { defaultValue: f.pose })}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
